import { CloseOutlined, SearchOutlined } from '@ant-design/icons'
import { Box, Card, Flex, Heading } from '@weareberyl/design-system'
import { Input, Typography } from 'antd'
import HeadTitle from '~/components/HeadTitle'
import { ModuleLink, VehicleLink } from '~/components/Link'
import BooleanIcon from '~/components/shared/BooleanIcon'
import StartDeployment from '~/components/Deployment/Start'
import Table, { ICustomColumnProps } from '~/components/Table'
import {
  FilterType,
  getSearchString,
  SearchText,
} from '~/components/Vehicle/List'
import {
  ModuleBasicFragment,
  ModuleHardware,
  ModuleWhere,
  useAllModulesQuery,
  Module,
  AllModulesQuery_table_ModuleConnection_nodes_Module,
} from '~/gql/generated/graphql'
import { useState } from 'react'
import { useQueryParams } from '~/utils/useQueryParams'
import { isDeploymentDisabled, moduleHardwareToString } from '~/utils'

const columns: ICustomColumnProps<ModuleBasicFragment>[] = [
  {
    title: 'Module ID',
    dataIndex: ['id'],
    render: (module_id: string, record: ModuleBasicFragment) =>
      record.vehicle?.detail?.scheme?.id ? (
        <VehicleLink
          id={module_id}
          bikeScheme={{ id: record.vehicle.detail.scheme.id }}
        />
      ) : (
        <ModuleLink id={module_id} />
      ),
  },
  {
    title: 'Unlock ID',
    dataIndex: ['unlock_id'],
  },
  {
    title: 'Hardware Type',
    dataIndex: ['hardware_type'],
    render: (value: ModuleHardware) => (
      <Typography.Text>{moduleHardwareToString(value)}</Typography.Text>
    ),
  },
  {
    title: 'Commissioned',
    dataIndex: ['vehicle', 'detail', 'scheme', 'id'],
    render: schemeId => <BooleanIcon value={!!schemeId} />,
  },
]

const ModuleList = () => {
  const [
    { filterField, filterType, searchValue, current, pageSize },
    setQueryParams,
  ] = useQueryParams('modules', {
    filterField: 'module_id',
    filterType: 'contains',
    searchValue: '',
  })

  const [searchInput, setSearchInput] = useState(searchValue)
  const [selectedRows, setSelectedRows] = useState<Module[]>([])

  const where: ModuleWhere[] = (() => {
    const searchString = getSearchString(filterType, searchValue)
    switch (filterField) {
      case 'module_id':
        return [
          {
            id: {
              ilike: searchString,
            },
          },
        ]
      case 'unlock_id':
        return [
          {
            unlock_id: {
              ilike: searchString,
            },
          },
        ]
      default:
        return []
    }
  })()

  const { data, loading } = useAllModulesQuery({
    variables: {
      where,
      paginate: { page: current, per_page: pageSize },
    },
  })

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: []) => {
      setSelectedRows(selectedRows)
    },
    getCheckboxProps: (
      record: AllModulesQuery_table_ModuleConnection_nodes_Module,
    ) => {
      return {
        disabled: isDeploymentDisabled(record.hardware_type),
      }
    },
  }

  return (
    <>
      <HeadTitle pageTitle="Modules" />
      <Card p={5}>
        <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
          <Heading variant="callout">Modules</Heading>
          <Box>
            <StartDeployment
              module_ids={selectedRows.map(r => r.id as string)}
            />
          </Box>
        </Flex>
        <Flex style={{ gap: '10px' }}>
          <Input
            placeholder="Search"
            value={searchInput}
            onChange={e => setSearchInput(e.currentTarget.value)}
            onPressEnter={() =>
              setQueryParams({
                searchValue: searchInput,
                current: 1,
              })
            }
            addonBefore={
              <FilterType
                onChange={([filterField, filterType]) =>
                  setQueryParams({
                    filterField,
                    filterType,
                  })
                }
                defaultValue={[filterField, filterType]}
                module
              />
            }
            prefix={<SearchOutlined />}
            suffix={<CloseOutlined onClick={() => setSearchInput('')} />}
          />
        </Flex>
        {searchValue && <SearchText text={searchValue} />}
        <Table
          id="modules-table"
          columns={columns}
          data={data ?? null}
          rowSelection={rowSelection}
          onChange={({ current, pageSize }) => {
            setQueryParams({
              current,
              pageSize,
            })
          }}
          loading={loading}
        />
      </Card>
    </>
  )
}

export default ModuleList
